import { useState } from 'react';

import { btn } from '@/components/ui/styles';
import type { LeadDetail } from '@/types/api';
import { formatDate, isNoEmail } from '@/utils/format';
import http from '@/utils/http';

/**
 * Whether a lead gets email updates (OUT-5, Q20): only once they agreed (they wrote to us
 * by email or through the form, agreed in Owner Hub, or the team notes it), and never
 * after they unsubscribed. Every email update ends with an unsubscribe link; here the team
 * turns them on or off when the lead asks (PUT /leads/{id}/email-updates). On a phone the
 * button takes the width, 44px tall (UI-1).
 */
export const EmailUpdatesControl = ({ lead, onChange }: { lead: LeadDetail; onChange: () => void }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const u = lead.email_updates;
  if (!u) return null;

  const set = async (on: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await http.put(`/leads/${encodeURIComponent(lead.id)}/email-updates`, { on });
      onChange();
    } catch {
      setError('That did not work. Try again.');
    } finally {
      setBusy(false);
    }
  };

  if (isNoEmail(lead.email)) return <p className="text-sm text-fg-muted">No email address yet, so nothing is sent.</p>;
  const since = (at: string | null) => (at ? `, ${formatDate(at)}` : '');
  const why = u.unsubscribed_at
    ? `They unsubscribed${since(u.unsubscribed_at)}.`
    : u.consent_via === 'email'
      ? `They wrote to us by email${since(u.consent_at)}.`
      : u.consent_via === 'team'
        ? `The team noted they agreed${since(u.consent_at)}.`
        : u.owner_marketing
          ? 'They agreed in Owner Hub.'
          : 'They have not agreed yet: they never wrote to us by email, nor agreed in Owner Hub.';

  return (
    <div>
      <p className="text-sm text-fg">
        {u.ok ? 'They get our email updates.' : 'They get no email updates.'} <span className="text-fg-muted">{why}</span>
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {u.ok ? (
          <button type="button" className={`${btn.secondary} flex-1 md:flex-none`} disabled={busy} onClick={() => void set(false)}>
            Stop their email updates
          </button>
        ) : (
          <button type="button" className={`${btn.secondary} flex-1 md:flex-none`} disabled={busy} onClick={() => void set(true)}>
            {u.unsubscribed_at ? 'They asked to get them again' : 'They agreed to email updates'}
          </button>
        )}
      </div>
      {error && <p className="mt-2 text-sm text-danger">{error}</p>}
    </div>
  );
};
